import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import toast from 'react-hot-toast';
import { adminOrdersApi, OrderDetail } from '../services/adminApi';
import DataTable from './components/DataTable';
import Modal from './components/Modal';
import Badge from './components/Badge';
import { ORDER_STATUS_TONES, ORDER_STATUSES } from './statusTones';

interface Order {
  id: string;
  order_number: string;
  customer_name: string;
  customer_email: string;
  status: string;
  created_at: string;
  notes?: string;
}

const fmt = (d: string) =>
  new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const fmtMoney = (amount: string | null, currency: string) =>
  amount != null ? `${Number(amount).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}` : '—';

// The backend stores structured delivery info as "Label: value" lines inside
// the single `notes` TEXT column (see order.service.ts's parseOrderNotes) —
// mirror that parsing here so the detail view shows it as fields, not a blob.
const ORDER_NOTE_KEYS = ['Adresse', 'Code postal', 'Pays', 'Période début', 'Période fin', 'Disponibilité', 'Contact préféré'];

function parseOrderNotes(notes?: string | null) {
  const raw = notes ?? '';
  const parsed: Record<string, string> = {};
  for (const line of raw.split('\n')) {
    const colon = line.indexOf(':');
    if (colon > -1) parsed[line.substring(0, colon).trim()] = line.substring(colon + 1).trim();
  }
  const message = raw.split('\n')
    .filter((l) => !ORDER_NOTE_KEYS.some((k) => l.startsWith(k + ':')))
    .join('\n').trim() || undefined;

  return {
    address: parsed['Adresse'],
    postalCode: parsed['Code postal'],
    country: parsed['Pays'],
    periodStart: parsed['Période début'],
    periodEnd: parsed['Période fin'],
    availability: parsed['Disponibilité'],
    contactMethod: parsed['Contact préféré'],
    message,
  };
}

// ── Read-only detail view: customer info, line items, delivery details, PDF ──
const OrderDetailModal: React.FC<{ orderId: string; onClose: () => void; onUnauthorized: () => void }> = ({
  orderId, onClose, onUnauthorized,
}) => {
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminOrdersApi.getOne(orderId)
      .then(setOrder)
      .catch((e: any) => {
        if (e.message === 'UNAUTHORIZED') { onUnauthorized(); return; }
        toast.error('Impossible de charger les détails de la commande');
        onClose();
      })
      .finally(() => setLoading(false));
  }, [orderId, onUnauthorized, onClose]);

  if (loading || !order) {
    return (
      <Modal onClose={onClose} maxWidth="max-w-2xl">
        <p className="py-8 text-center text-sm text-coffee-100">Chargement...</p>
      </Modal>
    );
  }

  const details = parseOrderNotes(order.notes);
  const hasDeliveryInfo = details.address || details.postalCode || details.country
    || details.periodStart || details.periodEnd || details.availability || details.contactMethod;

  return (
    <Modal onClose={onClose} maxWidth="max-w-2xl">
      <div className="mb-5 flex items-start justify-between">
        <div>
          <h2 className="font-display text-xl font-bold text-coffee-200">{order.order_number}</h2>
          <p className="text-xs text-coffee-100">{fmt(order.created_at)}</p>
        </div>
        <Badge tone={ORDER_STATUS_TONES[order.status] ?? 'gray'}>{order.status}</Badge>
      </div>

      {/* Customer */}
      <div className="mb-6 rounded-xl border border-cream-200 bg-cream-50 p-4">
        <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-coffee-100">Client</h3>
        <div className="text-sm font-medium text-coffee-200">{order.customer_name}</div>
        <div className="text-sm text-coffee-100">{order.customer_email}</div>
        {order.customer_phone && <div className="text-sm text-coffee-100">{order.customer_phone}</div>}
        {(order.customer_company || order.customer_country) && (
          <div className="text-sm text-coffee-100">
            {[order.customer_company, order.customer_country].filter(Boolean).join(' · ')}
          </div>
        )}
      </div>

      {/* Line items */}
      <div className="mb-6">
        <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-coffee-100">Produits commandés</h3>
        <div className="overflow-hidden rounded-xl border border-cream-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-cream-100 text-left text-xs text-coffee-100">
                <th className="px-3 py-2 font-medium">Produit</th>
                <th className="px-3 py-2 font-medium">Quantité</th>
                <th className="px-3 py-2 font-medium">Prix unitaire</th>
                <th className="px-3 py-2 font-medium">Total</th>
              </tr>
            </thead>
            <tbody>
              {order.items.map((item, i) => (
                <tr key={item.id} className={i % 2 === 0 ? 'bg-white' : 'bg-cream-50'}>
                  <td className="px-3 py-2 font-medium text-coffee-200">{item.product_name}</td>
                  <td className="px-3 py-2 text-coffee-100">{item.quantity_kg} {item.quantity_unit || 'kg'}</td>
                  <td className="px-3 py-2 text-coffee-100">{item.unit_price ? fmtMoney(item.unit_price, order.currency) : '—'}</td>
                  <td className="px-3 py-2 text-coffee-100">{item.total_price ? fmtMoney(item.total_price, order.currency) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {order.total_amount && (
          <p className="mt-2 text-right text-sm font-semibold text-coffee-200">
            Total : {fmtMoney(order.total_amount, order.currency)}
          </p>
        )}
      </div>

      {/* Delivery / availability details parsed from notes */}
      {hasDeliveryInfo && (
        <div className="mb-6">
          <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-coffee-100">Livraison & disponibilité</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 rounded-xl border border-cream-200 p-4 text-sm text-coffee-200">
            {details.address && <div><span className="text-coffee-100">Adresse : </span>{details.address}</div>}
            {details.postalCode && <div><span className="text-coffee-100">Code postal : </span>{details.postalCode}</div>}
            {details.country && <div><span className="text-coffee-100">Pays : </span>{details.country}</div>}
            {details.contactMethod && <div><span className="text-coffee-100">Contact préféré : </span>{details.contactMethod}</div>}
            {details.periodStart && <div><span className="text-coffee-100">Période début : </span>{details.periodStart}</div>}
            {details.periodEnd && <div><span className="text-coffee-100">Période fin : </span>{details.periodEnd}</div>}
            {details.availability && <div className="sm:col-span-2"><span className="text-coffee-100">Disponibilité : </span>{details.availability}</div>}
          </div>
        </div>
      )}

      {/* Free-form message */}
      {details.message && (
        <div className="mb-6">
          <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-coffee-100">Message</h3>
          <p className="whitespace-pre-line rounded-xl bg-cream-100 p-3 text-sm text-coffee-200">{details.message}</p>
        </div>
      )}

      <div className="flex gap-3 border-t border-cream-200 pt-4">
        <a
          href={adminOrdersApi.pdfUrl(order.id)}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 rounded-lg border border-cream-200 py-2.5 text-center text-sm font-medium text-coffee-200 transition-colors hover:bg-cream-100"
        >
          Télécharger le PDF
        </a>
        <button onClick={onClose} className="flex-1 rounded-lg bg-forest-200 py-2.5 text-sm font-semibold text-cream-50 transition-colors hover:bg-forest-100">
          Fermer
        </button>
      </div>
    </Modal>
  );
};

interface Props {
  onLogout: () => void;
  onCountChange?: (count: number) => void;
}

const AdminOrders: React.FC<Props> = ({ onLogout, onCountChange }) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [notes, setNotes] = useState('');
  const [detailOrderId, setDetailOrderId] = useState<string | null>(null);

  const fetchOrders = useCallback(async () => {
    try {
      const data = await adminOrdersApi.getAll(statusFilter || undefined);
      setOrders(data);
      onCountChange?.(data.length);
    } catch (e: any) {
      if (e.message === 'UNAUTHORIZED') onLogout();
      else toast.error('Impossible de charger les commandes');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, onLogout, onCountChange]);

  useEffect(() => { setLoading(true); fetchOrders(); }, [fetchOrders]);

  const handleStatus = async (id: string, status: string) => {
    try {
      await adminOrdersApi.updateStatus(id, status, notes || undefined);
      toast.success(`Commande mise à jour : ${status}`);
      setSelectedOrder(null);
      setNotes('');
      fetchOrders();
    } catch (e: any) {
      if (e.message === 'UNAUTHORIZED') { onLogout(); return; }
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const columns = useMemo<ColumnDef<Order, any>[]>(() => [
    {
      accessorKey: 'order_number',
      header: 'N° Commande',
      cell: (info) => <span className="font-mono text-xs font-semibold text-coffee-200">{info.getValue() as string}</span>,
    },
    {
      accessorKey: 'customer_name',
      header: 'Client',
      cell: (info) => (
        <div>
          <div className="font-medium text-coffee-200">{info.getValue() as string}</div>
          <div className="text-xs text-coffee-100">{info.row.original.customer_email}</div>
        </div>
      ),
    },
    {
      accessorKey: 'created_at',
      header: 'Date',
      cell: (info) => <span className="text-coffee-100">{fmt(info.getValue() as string)}</span>,
    },
    {
      accessorKey: 'status',
      header: 'Statut',
      cell: (info) => <Badge tone={ORDER_STATUS_TONES[info.getValue() as string] ?? 'gray'}>{info.getValue() as string}</Badge>,
    },
    {
      id: 'actions',
      header: '',
      enableSorting: false,
      cell: (info) => (
        <div className="flex gap-2">
          <button
            onClick={() => setDetailOrderId(info.row.original.id)}
            className="rounded-lg border border-cream-200 px-3 py-1.5 text-xs font-semibold text-coffee-200 transition-colors hover:bg-cream-100"
          >
            Voir détails
          </button>
          <button
            onClick={() => { setSelectedOrder(info.row.original); setNotes(''); }}
            className="rounded-lg bg-forest-200 px-3 py-1.5 text-xs font-semibold text-cream-50 transition-colors hover:bg-forest-100"
          >
            Gérer
          </button>
        </div>
      ),
    },
  ], []);

  return (
    <div>
      <DataTable
        columns={columns}
        data={orders}
        loading={loading}
        emptyMessage="Aucune commande"
        searchPlaceholder="Rechercher une commande..."
        toolbarExtra={
          <>
            <button
              onClick={() => setStatusFilter('')}
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${!statusFilter ? 'bg-forest-200 text-cream-50' : 'bg-white border border-cream-200 text-coffee-200 hover:bg-cream-100'}`}
            >
              Tous
            </button>
            {ORDER_STATUSES.map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`rounded-full px-3 py-1.5 text-xs font-medium capitalize transition-colors ${statusFilter === s ? 'bg-forest-200 text-cream-50' : 'bg-white border border-cream-200 text-coffee-200 hover:bg-cream-100'}`}
              >
                {s}
              </button>
            ))}
          </>
        }
      />

      {detailOrderId && (
        <OrderDetailModal
          orderId={detailOrderId}
          onClose={() => setDetailOrderId(null)}
          onUnauthorized={onLogout}
        />
      )}

      {selectedOrder && (
        <Modal onClose={() => setSelectedOrder(null)}>
          <div className="mb-4 flex items-start justify-between">
            <div>
              <h2 className="font-display text-xl font-bold text-coffee-200">{selectedOrder.order_number}</h2>
              <p className="text-sm text-coffee-100">{selectedOrder.customer_name} — {selectedOrder.customer_email}</p>
            </div>
            <Badge tone={ORDER_STATUS_TONES[selectedOrder.status] ?? 'gray'}>{selectedOrder.status}</Badge>
          </div>

          {selectedOrder.notes && (
            <div className="mb-4 whitespace-pre-line rounded-lg bg-cream-100 p-3 text-sm text-coffee-100">{selectedOrder.notes}</div>
          )}

          <div className="mb-4">
            <label className="mb-1 block text-sm font-medium text-coffee-200">Notes admin (optionnel)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full resize-none rounded-lg border border-cream-200 bg-cream-50 px-3 py-2 text-sm"
              placeholder="Ajouter une note..."
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            {ORDER_STATUSES.filter((s) => s !== selectedOrder.status).map((s) => (
              <button
                key={s}
                onClick={() => handleStatus(selectedOrder.id, s)}
                className={`rounded-lg px-3 py-2 text-sm font-medium capitalize transition-colors ${
                  s === 'confirmed' ? 'bg-blue-500 text-white hover:bg-blue-600'
                  : s === 'delivered' ? 'bg-green-600 text-white hover:bg-green-700'
                  : s === 'cancelled' ? 'bg-red-500 text-white hover:bg-red-600'
                  : 'bg-cream-200 text-coffee-200 hover:bg-cream-300'
                }`}
              >
                → {s}
              </button>
            ))}
          </div>

          <button onClick={() => setSelectedOrder(null)} className="mt-4 w-full rounded-lg border border-cream-200 py-2 text-sm text-coffee-100 hover:bg-cream-100">
            Fermer
          </button>
        </Modal>
      )}
    </div>
  );
};

export default AdminOrders;
